/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Button, Col, Row } from 'antd';
import classNames from 'classnames';
import { ActionButtonContainer } from 'components/DataCard/styled';
import Tab from 'components/Tab';
import TabPane from 'components/Tab/TabPane';
import { useRoutesDisplayValues } from 'hooks/useRoutesDisplayValues';
import useWindowSize from 'hooks/useWindowSize';
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { GeneralIcon, ProfileIcon } from 'routes/Profile/components/ProfileContent/styled';
import ROUTES from 'routes/routeConstants';
import { GeneralFunction } from 'types/common';
import { IService, ITransactionType } from 'types/services';

import CreateServiceModal from './ServiceModal';
import { MobileContainerHeaderStyled } from './styled';
import TransactionTypeDrawer from './TransactionTypeDrawer';
import CreateTransactionTypeModal from './TransactionTypeDrawer/TransactionTypeModal';

const mobileThreshold = 800;

const ServiceList: React.FC<{ services: IService[]; refetch: GeneralFunction }> = ({ services, refetch }) => {
  const [toggle, setToggle] = useState(true);
  const [activeTabKey, setActiveTabKey] = useState(services?.[0]?.id || '');
  const [modalVisible, setModalVisible] = useState<string>();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeService, setActiveService] = useState<IService | undefined>(services[0]);
  const [activeTransactionType, setActiveTransactionType] = useState<ITransactionType>();
  const servicesNumberRef = useRef(services.length);

  const { width } = useWindowSize();
  const isMobile = width < mobileThreshold;

  useEffect(() => {
    /// update active service everytime services object is updated
    // setActiveService(() => services.find((service) => service.id === activeService?.id) || services[0]);
    if (servicesNumberRef.current === services.length && services.length === 1) {
      setActiveTabKey(services[0].id);
      setActiveService(services[0]);
      setToggle(false);
    } else if (servicesNumberRef.current < services.length) {
      setActiveTabKey(services[services.length - 1].id);
      setActiveService(services[services.length - 1]);
      servicesNumberRef.current = services.length;
      setToggle(false);
    } else if (servicesNumberRef.current > services.length && services.length > 0) {
      setActiveTabKey(services[0].id);
      servicesNumberRef.current = services.length;
      setActiveService(services[0]);
      setToggle(false);
    } else if (!services || services.length === 0) {
      setActiveTabKey('');
      setActiveService(undefined);
      setToggle(true);
    }
  }, [services]);

  const onAddServiceClick = () => {
    setModalVisible('add-service-type');
  };

  const onAddTransactionTypeClick = () => {
    setDrawerVisible(true);
  };

  const closeModal = () => {
    setModalVisible(undefined);
    setActiveTransactionType(undefined);
  };

  const toggleMenu = (e: SyntheticEvent) => {
    e.stopPropagation();
    setToggle((c) => !c);
  };

  const hideNavBar = () => {
    setToggle(false);
  };

  const onTabClick = (key: string) => {
    hideNavBar();
    setActiveTabKey(key);
    setActiveService(services.find((service) => service.id === key) || services[0]);
  };

  const onEditServiceClick = () => {
    setModalVisible('edit-service-type');
  };

  const callbackOnCreateServiceSuccess = () => {
    closeModal();
  };

  const callBackOnEditServiceSuccess = () => {
    closeModal();
  };

  const onEditTransactionTypeClick = (transactionType: ITransactionType) => {
    setActiveTransactionType(transactionType);
    setDrawerVisible(true);
    // setModalVisible('edit-transaction-type');
  };

  const callBackOnEditTransactionTypeSuccess = () => {
    closeModal();
  };

  const onDeleteServiceClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    setModalVisible('delete-service-type');
  };

  const onDeleteTransactionClick = (e: SyntheticEvent, transactionType: ITransactionType) => {
    e.stopPropagation();
    setActiveTransactionType(transactionType);
    setModalVisible('delete-transaction-type');
  };

  const callbackOnDeleteServiceSuccess = () => {
    closeModal();
    setActiveTabKey(services[0].id);
    refetch();
  };

  const callbackOnDeleteTransactionTypeSuccess = () => {
    closeModal();
    refetch();
  };

  const closeDrawer = () => {
    setActiveTransactionType(undefined);
    setDrawerVisible(false);
  };

  const generateTransactionTypesRow = (transactionType: ITransactionType, index: number) => (
    <div
      className={`transaction-type-container ${activeTransactionType?.id === transactionType.id ? 'active' : ''}`}
      onClick={() => {
        onEditTransactionTypeClick(transactionType);
      }}
      key={index}>
      <div className='transaction-detail-container'>
        <div className='transaction-type-name'>{transactionType.name}</div>
        <div className='transaction-type-description'>{transactionType.description}</div>
        <div className='transaction-price-container'>
          <div className='price-inner-container'>
            <span className='client-tag'>Client</span>
            <div className='price-detail'>
              <span className='price-number'>${transactionType.defaultPrice?.clientInPersonFee}</span>
              <span>In Person,</span>
            </div>
            <div className='price-detail'>
              <span className='price-number'>${transactionType.defaultPrice?.clientRONFee}</span>
              <span>RON</span>
            </div>
          </div>
          <span className={`separator ${isMobile ? 'gx-d-none' : ''}`}>|</span>
          <div className='price-inner-container'>
            <span className='notary-tag'>Notary</span>
            <div className='price-detail'>
              <span className='price-number'>${transactionType.defaultPrice?.notaryGetInPerson}</span>
              <span>In Person,</span>
            </div>
            <div className='price-detail'>
              <span className='price-number'>${transactionType.defaultPrice?.notaryGetRON}</span>
              <span>RON</span>
            </div>
          </div>
        </div>
      </div>
      <div className='transaction-action-buttons-container'>
        <ActionButtonContainer
          onClick={(e) => {
            onDeleteTransactionClick(e, transactionType);
          }}
          style={{ width: 50 }}
          className='gx-dot gx-p-1 gx-mb-1'
          $iconOnHoverColor='red'
          $activeColor='transparent'>
          <i className='icon icon-trash gx-text-grey' />
        </ActionButtonContainer>
      </div>
    </div>
  );

  const isServiceMenuToggled = toggle || !services || services.length === 0;

  const generateServiceTabs = (service: IService) => (
    <TabPane
      tab={
        <div className='tab-content'>
          <div className='tab-content-header'>
            <span className='tab-content-header-title'>{service.name}</span>
            <span className='tab-content-header-extra'>{service.transactionTypes.length}</span>
            {/* <TabArrowIcon className='icon icon-charvlet-right' /> */}
          </div>
        </div>
      }
      key={service.id}>
      <div className='tab-body-container' style={{ height: '100%' }}>
        <div className='tab-body-header'>
          <div className='header-burger-container'>
            <button className='nav-bar-button' onClick={toggleMenu} type='button'>
              {isServiceMenuToggled ? <GeneralIcon size='10px' className='icon icon-charvlet-left' /> : <></>}
              <GeneralIcon size='20px' className='icon icon-menu' />
              {!isServiceMenuToggled ? <GeneralIcon size='10px' className='icon icon-charvlet-right' /> : <></>}
            </button>
          </div>
          <div className='title-container'>
            <span className='title'>{service.name}</span>
            <span className='subtitle'>{service.description}</span>
          </div>
          <div className='header-action-buttons-container'>
            <span className='edit-button' onClick={onEditServiceClick}>
              Edit
            </span>
            <span className='separator'>|</span>
            <span className='delete-button' onClick={onDeleteServiceClick}>
              Delete
            </span>
          </div>
        </div>
        <div className='tab-body-content'>
          <div className='add-button-container transaction-type'>
            <Button
              onClick={onAddTransactionTypeClick}
              style={{ color: '#038fde' }}
              type='text'
              className='add-transaction-type-button'>
              + Add Transaction Type
            </Button>
          </div>
          {service.transactionTypes.map(generateTransactionTypesRow)}
        </div>
      </div>
    </TabPane>
  );

  const mainTabClassName = classNames('service-page-container gx-h-100', {
    mobile: isMobile,
  });

  const routeDisplayValue = useRoutesDisplayValues()[ROUTES.SERVICE_TYPES];
  return (
    <>
      <CreateServiceModal
        refetch={refetch}
        closeModal={closeModal}
        callbackOnCreateSuccess={callbackOnCreateServiceSuccess}
        callbackOnEditSuccess={callBackOnEditServiceSuccess}
        callbackOnDeleteSuccess={callbackOnDeleteServiceSuccess}
        modalVisible={modalVisible}
        activeService={activeService}
      />
      <CreateTransactionTypeModal
        refetch={refetch}
        closeModal={closeModal}
        callbackOnCreateSuccess={closeModal}
        callbackOnEditSuccess={callBackOnEditTransactionTypeSuccess}
        callbackOnDeleteSuccess={callbackOnDeleteTransactionTypeSuccess}
        modalVisible={modalVisible}
        activeService={activeService}
        activeTransactionType={activeTransactionType}
      />
      <TransactionTypeDrawer
        activeTransactionType={activeTransactionType}
        activeService={activeService}
        drawerVisible={drawerVisible}
        closeDrawer={closeDrawer}
        refetch={refetch}
      />
      <MobileContainerHeaderStyled
        className={`${!isMobile ? 'gx-d-none' : undefined} mobile-service-header`}
        title={routeDisplayValue.text}
        headerIconName={routeDisplayValue.iconClass}
      />
      <Tab
        className={mainTabClassName}
        tabPosition='left'
        activeKey={`${activeTabKey}`}
        displayMenuInMobile={isServiceMenuToggled}
        onTabClick={onTabClick}
        onContentClick={hideNavBar}
        mobileThreshold={800}>
        <TabPane
          key='-1'
          tab={
            <>
              <Row align='middle' className='gx-mx-0 tab-header'>
                <Col>
                  <ProfileIcon className='icon icon-refer' />
                </Col>
                <Col>
                  <span>SERVICES</span>
                  {/* {isProfileChangeRequested && (
                  <Tooltip placement='rightTop' title='Change Requested'>
                    <ExclamationIcon className='icon icon-exclamation gx-text-danger' />
                  </Tooltip>
                )} */}
                </Col>
              </Row>
              <div className='add-button-container'>
                <Button onClick={onAddServiceClick} type='primary' className='add-service-button'>
                  + Add Service
                </Button>
              </div>
            </>
          }
          disabled
        />
        {services.map(generateServiceTabs)}
      </Tab>
    </>
  );
};

export default React.memo(ServiceList);
